import { Link } from "wouter";
import { PerspectiveArticle } from "@/components/PerspectiveArticle";
import { ARTICLE_CITATIONS } from "@/lib/citations";
import { AUTHORITATIVE_RESOURCES as REF } from "@/lib/links";
import { withNetworkUtm } from "@/lib/network";

const SLUG = "digital-mental-health-needs-more-than-convenience";
const TITLE = "Digital Mental Health Needs More Than Convenience";
const SEO_TITLE = "Digital Mental Health: Why Convenience Isn't Care, by Shariq Refai, MD";
const META_DESCRIPTION =
  "A board-certified psychiatrist on why most mental health apps fall short, what real telepsychiatry looks like, and how to tell convenience apart from care.";
const QUICK_ANSWER =
  "Digital mental health has spent the last decade selling convenience. Faster booking. Cheaper visits. Quicker prescriptions. Convenience matters, and the access problem in psychiatry is real. Convenience isn't care. The next generation of digital mental health has to deliver something the app stores haven't, which is real clinical depth, real continuity, and real safety. This is an educational read on what's working, what isn't, and what to look for when choosing a platform.";

const FAQS = [
  {
    q: "Is online psychiatry as effective as in person?",
    a: "For most conditions, the research is consistent. Depression and anxiety outcomes via telepsychiatry are comparable to in-person care. ADHD has been more contested, both clinically and from a regulatory standpoint, because diagnostic accuracy and controlled-substance prescribing both deserve in-person scrutiny in many cases. Severe psychotic disorders, complex eating disorders, and certain crisis situations are usually better served in person or through integrated care.",
  },
  {
    q: "How can a person tell if a digital mental health platform is legitimate?",
    a: "Look at the clinical model, not the marketing. Real evaluations, continuity of clinician, real follow-up, clear pricing, a defined safety pathway, and honest acknowledgment of what they don't address. If the platform can't answer those questions clearly, that's information.",
  },
  {
    q: "Are mental health apps a substitute for therapy or psychiatry?",
    a: "No. Apps can be useful supplements for tracking, education, and lower-intensity support. They cannot substitute for a clinical evaluation, ongoing psychiatric care, or therapy with a licensed clinician. Treating an app as care has cost real patients real outcomes.",
  },
  {
    q: "Can a chatbot or AI tool diagnose a person?",
    a: "No. AI tools may eventually play a role in screening, triage, or education. They are not equipped to diagnose, manage medication, or handle the full picture of a patient's mental health. A real diagnosis requires a licensed human clinician.",
  },
  {
    q: "What should an initial telepsychiatry appointment look like?",
    a: "A real first appointment should take 45 to 90 minutes and cover symptoms, history, sleep, appetite, energy, relationships, trauma, substance use, medications, physical health, and goals. The patient should leave with a working diagnosis or impression and a clear plan. If a first appointment was 15 minutes and ended with a prescription, that's a signal to reconsider where care is being received.",
  },
  {
    q: "Is telehealth psychiatry covered by insurance?",
    a: "Coverage has expanded significantly since 2020. Many insurance plans now cover telepsychiatry on par with in-person care. Specific coverage varies by plan and state. Always check benefits before booking.",
  },
];

const RELATED = [
  { title: "Why Popular Mental Health Commentary Falls Short in the Moment", href: "/perspectives/why-popular-mental-health-commentary-falls-short-in-the-moment" },
  { title: "The Difference Between a Thought and Thinking", href: "/perspectives/difference-between-a-thought-and-thinking" },
  { title: "Your Mind Is Full of Sh*t", href: "/books/your-mind-is-full-of-shit" },
  { title: "Havoc in Your Head", href: "/books/the-havoc-in-your-head" },
];

export default function PerspectiveDigitalMentalHealth() {
  return (
    <PerspectiveArticle
      slug={SLUG}
      citations={ARTICLE_CITATIONS[SLUG]}
      category="Digital Mental Health"
      title={TITLE}
      seoTitle={SEO_TITLE}
      metaDescription={META_DESCRIPTION}
      quickAnswer={QUICK_ANSWER}
      publishDate="2026-05-07"
      publishDateLabel="May 7, 2026"
      readTime="6 min read"
      ogImage={`/perspectives/${SLUG}/og.jpg`}
      inlineImage={`/perspectives/${SLUG}/inline.webp`}
      inlineImageAlt="Editorial illustration of a hand reaching toward a softly glowing screen with a single pulse line, representing the gap between digital convenience and real care."
      shrinkmdReading={[
        { before: "If you're comparing virtual psychiatry options, see ", anchor: "what actually matters in an online shrink", after: " on shrinkMD.", href: "https://shrinkmd.com/best-online-shrink?utm_source=shariqrefai&utm_medium=network_journey&utm_campaign=digital-mental-health-needs-more-than-convenience" },
      ]}
      wordCount={1487}
      keywords={[
        "digital mental health",
        "telepsychiatry",
        "online psychiatrist",
        "mental health apps",
        "online therapy quality",
        "telehealth mental health",
        "virtual psychiatric care",
      ]}
      about={["Telepsychiatry", "Digital Mental Health", "Psychiatry"]}
      faqs={FAQS}
      related={RELATED}
      furtherReading={
        <>
          <p>
            For deeper condition-specific reading, I serve as medical editor
            for four independent editorial publications:
          </p>
          <ul>
            <li>
              <a href={withNetworkUtm("https://anxietyresource.org/", "perspective_digital_mental_health_page")} target="_blank" rel="noopener noreferrer">
                AnxietyResource.org
              </a>
            </li>
            <li>
              <a href={withNetworkUtm("https://depressionresource.org/", "perspective_digital_mental_health_page")} target="_blank" rel="noopener noreferrer">
                DepressionResource.org
              </a>
            </li>
            <li>
              <a href={withNetworkUtm("https://anxietyresearch.org/", "perspective_digital_mental_health_page")} target="_blank" rel="noopener noreferrer">
                AnxietyResearch.org
              </a>
            </li>
            <li>
              <a href={withNetworkUtm("https://psychiatryrx.org/", "perspective_digital_mental_health_page")} target="_blank" rel="noopener noreferrer">
                PsychiatryRx.org
              </a>{" "}
              for plain-language, psychiatrist-reviewed guides to specific
              psychiatric and sleep medications
            </li>
          </ul>
          <p>All four are editorial and educational.</p>
          <p>
            For authoritative background from public health sources, see{" "}
            <a href={REF.nimhTechnology.url} target="_blank" rel="noopener noreferrer">
              {REF.nimhTechnology.name}
            </a>{" "}
            and{" "}
            <a href={REF.nimhCaringMentalHealth.url} target="_blank" rel="noopener noreferrer">
              {REF.nimhCaringMentalHealth.name}
            </a>
            .
          </p>
        </>
      }
    >
      <h2 id="why-this-matters">Why does this conversation matter?</h2>
      <p>
        The access gap in psychiatric care has been a structural problem for decades. Patients on four-month waitlists. People in rural counties without a psychiatrist within a hundred miles. Working professionals locked out by a 2 p.m. appointment slot. Telehealth changed the equation. That's a real win.
      </p>
      <p>
        The convenience layer of digital mental health filled a real gap. A portion of the patients who used those services have ended up in worse positions than if they'd stayed on a waitlist, because the convenience model often confused convenience with care. That's the educational tension worth writing about.
      </p>
      <p>
        This isn't an argument against telepsychiatry. Telepsychiatry done well is some of the most accessible and effective psychiatric care available. The argument is against models that strip out the depth that makes care actually work.
      </p>

      <h2 id="what-convenience-solves">What has digital mental health gotten right?</h2>
      <p>The digital wave has earned its credit.</p>
      <p>
        Access used to be the largest unsolved problem in mental health. A patient in a small town with no local psychiatrist had nothing. A working professional with a non-flexible schedule had nothing. A parent without childcare had nothing. The waitlists for in-person psychiatry stretched for months. Insurance limited choice. Stigma kept people from going to the practice down the street even when there was one.
      </p>
      <p>
        Telehealth changed all of that. Video-based psychiatric care, when it's done correctly, is not inferior to in-person care for the conditions most commonly addressed this way. The research is strong. The patient experience is often better. People are more comfortable in their own homes. They show up for appointments more reliably. The barrier to starting care drops by an order of magnitude.
      </p>
      <p>That's the win. The win is real.</p>
      <p>
        The model only works if the care behind it is real. That's where most of the field is still learning.
      </p>

      <h2 id="what-gets-wrong">Where does most digital mental health fall short?</h2>
      <p>Speaking plainly, because patients deserve it.</p>
      <h3>The 10-minute intake</h3>
      <p>
        A 10-minute intake form cannot replace a real psychiatric evaluation. It can't ask the questions that matter. It can't notice the silences. It can't follow the thread that leads from one symptom to the actual underlying picture. Some platforms have built a business model around the 10-minute intake because it scales. Scale isn't a clinical strategy. It's a financial one.
      </p>
      <h3>The disposable clinician</h3>
      <p>
        A model where a patient sees a different clinician each visit is not real psychiatric care. Continuity matters. The clinician who saw a patient in March and remembers what was said in March is the clinician who can tell whether the change in May is meaningful. The patient who has to re-explain their entire history every visit is the patient whose care never deepens.
      </p>
      <h3>The medication-only frame</h3>
      <p>
        Some platforms exist primarily to get patients onto medication. Medication is a real tool. It's also one tool among many. A platform that doesn't talk about therapy, sleep, substance use, relationships, and life context is a platform that's optimizing for prescriptions, not outcomes.
      </p>
      <h3>The "wellness" content layer</h3>
      <p>
        The line between mental health support and wellness content has been blurred to the point of being misleading. Meditation videos, mindfulness exercises, and journaling prompts are useful supplements. They are not <Link href="/perspectives/why-we-present-care-as-five-options-not-one">psychiatric care</Link>. They cannot replace a clinical evaluation. A platform that markets itself as mental health care but delivers mostly wellness content is selling something other than care.
      </p>
      <h3>The chatbot creep</h3>
      <p>
        AI tools have a place in healthcare. The place is not as a replacement for a clinical conversation. The place is, at best, as a triage layer, an educational layer, or an administrative layer. A chatbot is not a therapist. A chatbot is not equipped to handle the moment when a patient discloses suicidal thinking. A chatbot cannot read a face. The push to put AI in front of patients as the primary touch point is the next mistake the field is going to have to walk back.
      </p>
      <h3>The follow-up gap</h3>
      <p>
        A platform that gets a patient onto a medication and then disappears for three months is not delivering care. <a href={withNetworkUtm("https://psychiatryrx.org/", "perspective_digital_mental_health_page")} target="_blank" rel="noopener noreferrer">SSRIs, SNRIs, mood stabilizers, and stimulants</a> need follow-up. Side effects need to be monitored. Dose changes need a clinician's eye. The handoff between the prescription and the next appointment is where harm happens, and the platforms that minimize that contact in the name of cost are taking on risk they're not pricing for.
      </p>

      <h2 id="what-real-care-looks-like">What does responsible digital mental health look like?</h2>
      <p>
        The version the next decade should look like, and the version{" "}
        <a href={withNetworkUtm("https://shrinkmd.com/anxiety-disorders", "perspective_digital_mental_health_page")} target="_blank" rel="noopener noreferrer">shrinkMD</a>
        {" "}is built to deliver.
      </p>
      <h3>Real evaluations</h3>
      <p>
        A psychiatric evaluation should take time. It should ask about sleep, appetite, energy, relationships, trauma history, substance use, medications, physical health, and goals. If a platform can't tell you what their initial evaluation involves and how long it takes, that's a signal.
      </p>
      <h3>Continuity of clinician</h3>
      <p>
        A patient should be able to see the same psychiatrist or nurse practitioner over time. The relationship is part of the care. If the platform's model is "match you with the next available," that's urgent care, not psychiatric care.
      </p>
      <h3>Medication and care coordination</h3>
      <p>
        A real platform talks to a patient's therapist if there is one. It coordinates with the primary care doctor. It doesn't operate in a silo. The patient is one person with one nervous system. The care has to reflect that.
      </p>
      <h3>Safety infrastructure</h3>
      <p>
        A real platform has a way to handle the patient who is in crisis, who is having a side effect, who needs urgent attention. There is a clear path to a human. There is a real escalation pathway. The patient is not left alone with a chatbot when something is wrong.
      </p>
      <h3>Transparent pricing</h3>
      <p>
        Patients should know what an evaluation costs, what a follow-up costs, what insurance covers, and what they will pay out of pocket. Surprise charges are not just bad business. They erode the trust the entire clinical relationship is built on.
      </p>
      <h3>Respect for the limits of telehealth</h3>
      <p>
        Some patients need in-person care. Severe psychotic disorders, complex bipolar presentations, eating disorders, and certain emergency situations are best served in person or through integrated care. A real telepsychiatry practice knows where its competence ends and refers out when needed. A platform that pretends to handle everything is overpromising.
      </p>

      <p>
        shrinkMD publishes these expectations as a written standard: <a href="https://shrinkmd.com/digital-psychiatry-index?utm_source=shariqrefai&utm_medium=network_journey&utm_campaign=digital-mental-health-needs-more-than-convenience" target="_blank" rel="noopener noreferrer" className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent">the Digital Psychiatry Index, shrinkMD's standards framework for responsible telepsychiatry</a>. shrinkMD is part of The Shrink Network, founded by Shariq Refai, MD.
      </p>

      <h2 id="what-to-ask">What should I ask a digital mental health platform before signing up?</h2>
      <p>
        Marketing budget tells a person nothing about clinical depth. Useful questions to ask any digital mental health service before signing up.
      </p>
      <p>
        How long is your initial evaluation? Will I see the same clinician each visit? How is medication managed between appointments? What happens if I have a side effect or a crisis? How do you coordinate with my therapist or primary care? What conditions don't you treat?
      </p>
      <p>
        A real practice can answer those questions in plain language. A platform optimized for funnel can't.
      </p>
      <p>
        A reasonable trust check after a first appointment is whether the patient felt heard, whether the clinician asked questions that mattered, and whether they left with a clear sense of what was happening and what would happen next. If yes, that's a green light. If they felt rushed, the diagnosis was handed over in five minutes, the medication was prescribed before the conversation was finished, that's not the right place.
      </p>

      <h2 id="future">What's the future worth building toward?</h2>
      <p>Looking at the field, here's what the next several years should bring.</p>
      <p>
        <strong>Better integration with primary care and therapy.</strong> The artificial split between mental and physical health, and between medication and therapy, is costing patients real outcomes. The future is integrated.
      </p>
      <p>
        <strong>Honest conversation about AI's role.</strong> AI tools will play a growing role in education, triage, administration, and possibly some forms of low-intensity support. They will not replace clinical care. The field needs to draw the line clearly so patients don't end up trusting a chatbot with the moments that need a human.
      </p>
      <p>
        <strong>Better outcomes data.</strong> Most digital mental health companies report engagement metrics. Engagement is not outcome. The field needs to publish real outcome data on the conditions it claims to address. Patients deserve that. Investors should demand it. Regulators will eventually require it.
      </p>
      <p>
        <strong>Better privacy.</strong> Mental health data is some of the most sensitive data a person produces. The handling of that data in the digital health space has not been good enough. The patients who trust a platform with their lowest moments deserve to know exactly where that data goes and who can see it.
      </p>
      <p>
        <strong>Real cost transparency.</strong> The pricing models in digital mental health are opaque, and the opacity costs trust. The future is clear pricing, clear coverage, and patients knowing what they will pay before they show up.
      </p>
      <p>
        The field has a choice in front of it. Keep optimizing for convenience and charge a price patients will eventually figure out. Or raise the standard, deliver real care at scale, and earn the trust the convenience-only era is starting to lose.
      </p>
      <p>
        The longer educational view on emotional health lives in{" "}
        <Link href="/books/your-mind-is-full-of-shit"><em>Your Mind Is Full of Sh*t</em></Link>
        {" "}and{" "}
        <Link href="/books/the-havoc-in-your-head"><em>Havoc in Your Head</em></Link>
        , available through shrinkMD Publishing.
      </p>
    </PerspectiveArticle>
  );
}
